import {
    IconBook,
    IconBookmarkQuestion,
    IconChevronRight,
    IconFileCertificate,
    IconFilePlus,
    IconHome,
    IconSettings,
    IconLogout2,
    IconUser,
    IconMenu2,
    IconX,
    IconMessage2,
    IconThumbDownOff,
    IconExchange,
    IconDeviceTv,
    IconAlertCircle
} from '@tabler/icons-react';

import { SidebarButton } from './components';
import { useCallback, useEffect, useState } from 'react';
import { SidebarUser } from './components/SidebarUser/SidebarUser';
import clsx from 'clsx';
import { PROTECTED_ROUTES } from '../../constants/routes';
import { useAuth } from '../../hooks/useAuth';
import { RoleEnum } from '../../types/role';

import Logo from '../../assets/cf-logo.png';
import { useBranch } from '../../hooks/useBranch';
import { Modal } from 'antd';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import './Sidebar.scss';

const Sidebar = () => {
    const { logout, user } = useAuth();
    const { removeBranch } = useBranch();
    const { theme } = useTheme();
    // isOpen (colapsado/expandido en escritorio) vive en SidebarContext
    // para que Screen pueda alinear el margen del contenido con el
    // ancho real del sidebar.
    const { isOpen, toggleSidebar } = useSidebar();

    const [alertModal, setAlertModal] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const toggleMobileMenu = useCallback(() => {
        setMobileMenuOpen((prev) => !prev);
    }, []);

    useEffect(() => {
        const advised = localStorage.getItem('mallorcaoposita-advisor');

        if (!advised) {
            setAlertModal(true);
            localStorage.setItem('mallorcaoposita-advisor', 'true');
        }
    }, []);

    const isAdmin = (user?.role?.name === RoleEnum.superadmin || user?.role?.name === RoleEnum.admin);

    return (
        <>
            {/* Mobile Header */}
            <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-[#1a1f2e] border-b border-gray-200 dark:border-white/5 flex items-center justify-between px-4 z-[var(--z-sidebar)]">
                <img src={Logo} alt="Logo" className="h-8 w-auto invert dark:invert-0" />
                <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
                >
                    {mobileMenuOpen ? <IconX size={24} /> : <IconMenu2 size={24} />}
                </button>
            </div>

            {/* Mobile Overlay */}
            {mobileMenuOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black/50 z-[var(--z-overlay)] backdrop-blur-sm"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar Container */}
            <aside
                className={clsx(
                    "fixed top-0 left-0 h-screen bg-white dark:bg-[#1a1f2e] border-r border-gray-200 dark:border-white/5 shadow-xl z-[var(--z-sidebar)] transition-all duration-300 ease-in-out flex flex-col",
                    // Desktop width logic
                    "hidden md:flex",
                    isOpen ? "w-72" : "w-20",
                    // Mobile slide-over logic (overrides desktop hidden if mobile menu open)
                    mobileMenuOpen && "!flex w-72"
                )}
            >
                {/* Logo Area */}
                <div className={clsx("p-6 flex items-center justify-center transition-all duration-300", !isOpen && "p-4")}>
                    <img
                        src={Logo}
                        alt="Logo"
                        className={clsx(
                            "transition-all duration-300 invert dark:invert-0",
                            isOpen ? "h-12 w-auto" : "h-8 w-auto"
                        )}
                    />
                </div>

                {/* User Info */}
                <SidebarUser sidebarOpen={isOpen || mobileMenuOpen} />

                {/* Navigation Sections */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                    <div className="flex flex-col gap-1">
                        {/* Desktop Toggle Button */}
                        <div className="hidden md:block">
                            <SidebarButton
                                label={isOpen ? "Contraer" : "Expandir"}
                                sidebarOpen={isOpen || mobileMenuOpen}
                                onClick={toggleSidebar}
                            >
                                <IconChevronRight size={20} className={clsx("transition-transform duration-300", isOpen && "rotate-180")} />
                            </SidebarButton>
                        </div>

                        <SidebarButton label="Dashboard" sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.DASHBOARD} onClick={() => setMobileMenuOpen(false)}>
                            <IconHome size={20} />
                        </SidebarButton>

                        {user?.testSection && (
                            <>
                                <SidebarButton label="Iniciar test" sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.START_TEST} onClick={() => setMobileMenuOpen(false)}>
                                    <IconFilePlus size={20} />
                                </SidebarButton>
                                <SidebarButton label="Mis resultados" sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.RESULTS} onClick={() => setMobileMenuOpen(false)}>
                                    <IconFileCertificate size={20} />
                                </SidebarButton>
                                <SidebarButton label="Preguntas destacadas" sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.SAVED_QUESTIONS} onClick={() => setMobileMenuOpen(false)}>
                                    <IconBookmarkQuestion size={20} />
                                </SidebarButton>
                            </>
                        )}

                        {user?.temarySection && (
                            <SidebarButton label='Temario' sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.SUBJECTS} onClick={() => setMobileMenuOpen(false)}>
                                <IconBook size={20} />
                            </SidebarButton>
                        )}

                        <SidebarButton label='Clases online' sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.ONLINE_SESSIONS} onClick={() => setMobileMenuOpen(false)}>
                            <IconDeviceTv size={20} />
                        </SidebarButton>

                        {isAdmin && (
                            <SidebarButton label='Preguntas Impugnadas' sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.IMPUGNATED_QUESTIONS} onClick={() => setMobileMenuOpen(false)}>
                                <IconThumbDownOff size={20} />
                            </SidebarButton>
                        )}

                        <SidebarButton label='Foro' sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.FORUM} onClick={() => setMobileMenuOpen(false)}>
                            <IconMessage2 size={20} />
                        </SidebarButton>
                    </div>

                    {/* Divider */}
                    <div className="my-4 border-t border-gray-100 dark:border-white/5 mx-2"></div>

                    {/* Bottom Actions */}
                    <div className="flex flex-col gap-1">
                        <SidebarButton label='Atención' sidebarOpen={isOpen || mobileMenuOpen} onClick={() => setAlertModal(true)}>
                            <IconAlertCircle size={20} />
                        </SidebarButton>

                        {isAdmin && (
                            <SidebarButton label='Administrar' sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.ADMIN} onClick={() => setMobileMenuOpen(false)}>
                                <IconSettings size={20} />
                            </SidebarButton>
                        )}

                        <SidebarButton label='Perfil' sidebarOpen={isOpen || mobileMenuOpen} link={PROTECTED_ROUTES.PROFILE} onClick={() => setMobileMenuOpen(false)}>
                            <IconUser size={20} />
                        </SidebarButton>

                        <SidebarButton label='Cambiar de rama' sidebarOpen={isOpen || mobileMenuOpen} onClick={removeBranch}>
                            <IconExchange size={20} />
                        </SidebarButton>

                        <SidebarButton label='Cerrar sesión' sidebarOpen={isOpen || mobileMenuOpen} onClick={logout}>
                            <IconLogout2 size={20} />
                        </SidebarButton>
                    </div>
                </div>
            </aside>

            {alertModal && (
                <Modal
                    open={alertModal}
                    onCancel={() => setAlertModal(false)}
                    onOk={() => setAlertModal(false)}
                    okText="Aceptar"
                    title={
                        <div className={clsx(
                            'flex items-center gap-3',
                            'text-gray-900 dark:text-white'
                        )}>
                            <div className={clsx(
                                'w-10 h-10 rounded-lg flex items-center justify-center',
                                'bg-gradient-to-br from-amber-500 to-orange-500',
                                'dark:from-amber-600 dark:to-orange-600'
                            )}>
                                <IconAlertCircle size={20} className="text-white" />
                            </div>
                            <span className="text-xl font-bold">Atención</span>
                        </div>
                    }
                    centered={false}
                    className={clsx(
                        'alert-modal',
                        theme === 'dark' ? 'dark-modal' : 'light-modal'
                    )}
                    style={{
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }}
                    maskStyle={{
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)'
                    }}
                    wrapClassName={clsx(
                        'alert-modal-wrapper',
                        theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
                        'bottom-sheet-mobile'
                    )}
                    okButtonProps={{
                        className: clsx(
                            'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
                            'border-none shadow-md hover:shadow-lg',
                            'transition-all duration-300'
                        )
                    }}
                    cancelButtonProps={{
                        className: clsx(
                            'border-gray-300 dark:border-gray-600',
                            'text-gray-700 dark:text-gray-300',
                            'hover:border-gray-400 dark:hover:border-gray-500',
                            'transition-all duration-300'
                        )
                    }}
                >
                    <p className="text-gray-600 dark:text-gray-300">
                        {'Esta página web almacena información sobre tu dispositivo en la memoria de tu navegador. Esto significa que si vas a eliminar los datos de navegación y no excluyes este sitio de ese borrado, perderás esos datos y por tanto, el acceso a la plataforma y tendrás que contactar con el administrador para que habilite de nuevo tu acceso mediante este navegador. Por ello, asegurate de no borrar los datos de esta web.'}
                    </p>
                </Modal>
            )}
        </>
    );
};

export { Sidebar };
